/**
 * Formats a number into Indian currency format.
 * @param {number} amount
 * @returns {string}
 */
export const formatToCurrency = (amount) => {
  // Handle null, undefined, empty string, or not a finite number
  if (amount === null || amount === undefined || amount === '' || isNaN(Number(amount))) return '';
  // Convert to number (handles numeric strings too)
  const num = Number(amount);
  return num.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });
};

// ─── IST Date / Time Helpers ─────────────────────────────────────────────────
// All display timestamps use Asia/Kolkata regardless of the user's browser TZ.

const IST = 'Asia/Kolkata';

/**
 * Format a date to IST date string.
 * e.g. "23 Feb 2026"
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const formatDateIST = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: IST,
  });
};

/**
 * Format a date to IST time string.
 * e.g. "02:30 PM"
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const formatTimeIST = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: IST,
  });
};

/**
 * Format a date to IST date + time string.
 * e.g. "Feb 23, 2026 2:30 PM"
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const formatDateTimeIST = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: IST,
  });
};

/**
 * Returns a relative "time ago" string in IST context.
 * e.g. "2 hours ago", "3 days ago", "just now"
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const timeAgoIST = (dateInput) => {
  if (!dateInput) return '';
  const diff = Date.now() - new Date(dateInput).getTime();
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateIST(dateInput);
};

/**
 * Formats a date into "Jun 2, 2025 8:30AM" format (IST).
 * @param {Date|string|number} dateInput
 * @returns {string}
 */
export const formatToReadableDateTime = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';

  // Use IST-aware formatter
  return date.toLocaleString('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: IST,
  });
};

export const techStacksList = ["", "JavaScript", "Java", "Spring", "Spring Boot", "Hibernate", "Kafka", "Kubernetes", "Microservices", "AWS", "Docker", "Python", "SQL", "React", "Node.js", "HTML/CSS", "JIRA", "Git", "Agile", "TypeScript", "Angular", "Machine Learning", "Data Science"];

/**
 * Converts a number or comma-separated string to Indian currency format.
 * @param {number|string} value - Number or string like "123,234"
 * @returns {string}
 */
export const convertToIndianCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // If it's a string with commas, remove them first
  let cleanValue = typeof value === 'string' ? value.replace(/,/g, '') : value;
  
  // Convert to number and validate
  const num = Number(cleanValue);
  if (isNaN(num)) return '';
  
  return num.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });
};

// Example usage:
// console.log(formatToCurrency(1234567.89)); // ₹12,34,567.89
